import React, { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import { resolveCustomerReview } from "../../../../services/customerReview";

export const isCustomerPopupReviewAdministrator = (role) =>
  ["administrator", "admin", "superadmin"].includes(
    String(role || "").trim().toLowerCase().replace(/_/g, "-"),
  );

export default function CustomerReviewShortcut({
  customer,
  userRole,
  instanceUniqueId,
  onRefresh,
}) {
  const [decision, setDecision] = useState(null);
  const [reviewerNote, setReviewerNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const pendingReview = customer?.currentReview?.status === "pending"
    ? customer.currentReview
    : null;
  const canReview = isCustomerPopupReviewAdministrator(userRole);

  if (!pendingReview) return null;

  const closeDialog = () => {
    if (submitting) return;
    setDecision(null);
    setReviewerNote("");
    setError("");
  };

  const submit = async () => {
    if (!decision || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await resolveCustomerReview({
        customerId: customer.id,
        action: decision === "approve" ? "approve" : "decline",
        comment: reviewerNote.trim(),
        instanceUniqueId,
        source: "customer-popup",
      });
      await onRefresh?.();
      toast.success(decision === "approve" ? "Customer approved" : "Customer review rejected");
      setDecision(null);
      setReviewerNote("");
    } catch (requestError) {
      const message = requestError?.response?.data?.message || "Failed to submit customer review.";
      setError(message);
      toast.error(message);
      try {
        await onRefresh?.();
      } catch (_refreshError) {
        // Preserve the authoritative resolution error and allow a retry.
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Stack spacing={1}>
          <Typography variant="subtitle2">Customer review pending</Typography>
          <Typography variant="body2">This Lead → Customer conversion is awaiting review.</Typography>
          {canReview && (
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="contained" color="success" onClick={() => setDecision("approve")}>Approve</Button>
              <Button size="small" variant="outlined" color="error" onClick={() => setDecision("reject")}>Reject</Button>
            </Stack>
          )}
        </Stack>
      </Alert>
      <Dialog open={Boolean(decision)} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{decision === "approve" ? "Approve Customer" : "Reject Customer"}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {decision === "approve"
              ? "You are about to approve this customer review. Approving will complete the pending Lead → Customer review and make this customer available under the normal approved customer workflow."
              : "You are about to reject this customer review. The pending Lead → Customer conversion will be rejected according to the existing customer review workflow."}
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            label="Reason / review note"
            placeholder={decision === "approve"
              ? "Add an optional reason or internal note for this approval..."
              : "Explain why this customer conversion is being rejected..."}
            value={reviewerNote}
            onChange={(event) => setReviewerNote(event.target.value)}
            multiline
            minRows={3}
            fullWidth
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>Cancel</Button>
          <Button variant="contained" color={decision === "approve" ? "success" : "error"} onClick={submit} disabled={submitting}>
            {submitting ? "Submitting..." : decision === "approve" ? "Approve Customer" : "Reject Customer"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
